import type { CredentialRequirement, FeatureModel } from '@/adapters/apiv1.g';
import type {
  CredentialRequirement as CredentialRequirementCore,
  Feature,
} from '@/types/core/provider';
import type { JSONSchema } from '@/types/shared/json-schema';
import type { UUID } from '@/utils/uuid';

/**
 * Maps the backend credential slots onto core credential requirements, skipping
 * entries without a slot name. Returns undefined when no slots are declared.
 */
function extractCredentials(
  credentials: ReadonlyArray<CredentialRequirement> | undefined
): CredentialRequirementCore[] | undefined {
  if (!credentials?.length) return undefined;
  const mapped = credentials
    .filter((credential) => !!credential.name)
    .map((credential) => ({
      name: credential.name!,
      templateId: credential.template_id as UUID | undefined,
      templateName: credential.template_name,
    }));
  return mapped.length > 0 ? mapped : undefined;
}

/**
 * Transform a backend FeatureModel to a frontend Feature type.
 */
export function extractProviderFeature(model: FeatureModel): Feature {
  // Parse options from JSON string if present
  let optionsSchema: JSONSchema | undefined;
  if (model.options) {
    try {
      if (typeof model.options === 'string') {
        optionsSchema = JSON.parse(model.options) as JSONSchema;
      } else {
        optionsSchema = model.options as JSONSchema;
      }
    } catch (err) {
      console.error('Failed to parse options schema:', err);
      optionsSchema = undefined;
    }
  }

  return {
    _rev: model._rev,
    id: (model.feature_id ?? '') as UUID,
    providerId: (model.provider_id ?? '') as UUID,
    name: model.name ?? '',
    description: model.description ?? '',
    requirements: model.requirements ?? [],
    optionsSchema,
    credentials: extractCredentials(model.credentials),
  };
}
